import React, { useRef, useState, useMemo, useEffect } from "react";
import JoditEditor from "jodit-react";
import { MdDesktopWindows, MdPhoneIphone, MdOutlineEmail, MdDrafts, MdFullscreenExit } from "react-icons/md";
import Modal from "./Modal";
import "../../Styles/TextEditor.css";

function TextEditor({
  content,
  setContent,
  onBlur,
  showPreview = false,
  placeholder = "Write something...",
  className = "",
  isInvalid = false,
  subject = "",
}) {
  const editor = useRef(null);
  const [internalContent, setInternalContent] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [isFullScreen, setIsFullScreen] = useState(false);

  const currentContent = content !== undefined ? content : internalContent;
  const handleChange = setContent !== undefined ? setContent : setInternalContent;
  const editorClassName = `${className} ${isInvalid ? "border border-danger rounded-2" : ""}`.trim();

  const contentRef = useRef(currentContent);
  contentRef.current = currentContent;

  const subjectRef = useRef(subject);
  subjectRef.current = subject;

  useEffect(() => {
    contentRef.current = currentContent;
  }, [currentContent]);

  useEffect(() => {
    if (isFullScreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isFullScreen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isFullScreen) {
        setIsFullScreen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullScreen]);

  const isFullScreenRef = useRef(isFullScreen);
  isFullScreenRef.current = isFullScreen;

  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: placeholder || "Start typing...",
      enableDragAndDropFileToEditor: true,
      uploader: {
        insertImageAsBase64URI: true,
      },
      hidePoweredByJodit: true,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      defaultActionOnPaste: 'insert_only_text',
      defaultActionOnPasteFromWord: 'insert_only_text',
      processPasteHTML: false,
      showXPathInStatusbar: false,
      showCharsCounter: true,
      showWordsCounter: true,
      removeButtons: ["about"],
      disablePlugins: ["about", "powered-by-jodit", "poweredByJodit", "fullsize"],
      buttons: [
        "bold", "italic", "underline", "strikethrough", "eraser", "|",
        "ul", "ol", "|",
        "font", "fontsize", "paragraph", "classSpan", "lineHeight", "|",
        "superscript", "subscript", "|",
        "file", "image", "video",
        "\n",
        "spellcheck", "speechRecognize", "|",
        "cut", "copy", "paste", "selectall", "copyformat", "|",
        "hr", "table", "link", "symbol", "math", "|",
        "left", "center", "right", "justify", "|",
        "brush", "|",
        "undo", "redo", "|",
        "find", "|",
        "source", "fullsize", "preview", "print"
      ],
      toolbarAdaptive: false,
      controls: {
        fullsize: {
          icon: "fullsize",
          tooltip: "Toggle Fullscreen (Esc)",
          exec: () => {
            setIsFullScreen((prev) => !prev);
          },
        },
        preview: {
          icon: "eye",
          tooltip: "Preview",
          exec: (joditInstance) => {
            let html = "";
            if (joditInstance && typeof joditInstance.getEditorValue === "function") {
              html = joditInstance.getEditorValue();
            }
            if (!html && joditInstance && typeof joditInstance.value === "string") {
              html = joditInstance.value;
            }
            if (!html && editor.current) {
              if (typeof editor.current.getEditorValue === "function") {
                html = editor.current.getEditorValue();
              } else if (typeof editor.current.value === "string") {
                html = editor.current.value;
              }
            }
            if (!html) {
              html = contentRef.current || "";
            }
            setPreviewHtml(html || "");
            setIsPreviewOpen(true);
          },
        },
      },
    }),
    [placeholder]
  );

  const hasPreviewContent = Boolean(
    previewHtml &&
    previewHtml.trim().length > 0 &&
    previewHtml.trim() !== "<p><br></p>" &&
    previewHtml.trim() !== "<p></p>" &&
    previewHtml.trim() !== "<br>"
  );

  return (
    <div className={`text-editor-container ${isFullScreen ? "text-editor-fullscreen-mode" : ""}`}>
      {isFullScreen && (
        <div className="text-editor-fullscreen-header">
          <div className="d-flex align-items-center gap-2">
            <span className="text-editor-fullscreen-title">
              {subjectRef.current?.trim() || "Email Template Editor"}
            </span>
            <span className="badge bg-light text-dark border">Fullscreen</span>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-light border d-flex align-items-center gap-1 shadow-sm px-3"
            onClick={() => setIsFullScreen(false)}
            title="Exit Fullscreen (Esc)"
          >
            <MdFullscreenExit size={18} />
            <span>Exit Fullscreen</span>
          </button>
        </div>
      )}

      <div className={editorClassName}>
        <JoditEditor
          ref={editor}
          value={currentContent}
          config={config}
          onBlur={(newContent) => {
            handleChange(newContent);
            if (onBlur) {
              onBlur(newContent);
            }
          }}
          onChange={() => {}}
        />
      </div>

      {showPreview && (
        <div style={{ marginTop: "20px" }}>
          <h5 className="fw-bold" style={{ marginBottom: "10px" }}>HTML Output</h5>
          <pre
            className="rounded-3 overflow-y-auto"
            style={{
              background: "var(--hc-gray-bg-soft)",
              padding: "12px",
              maxHeight: "300px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {currentContent}
          </pre>
        </div>
      )}

      <Modal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title="Email Template Preview"
        subtitle="Live simulated view of how this email appears to recipients"
        icon={<MdOutlineEmail size={22} />}
        size="lg"
        scrollable={true}
        footer={
          <div className="d-flex justify-content-between align-items-center w-100">
            <span className="small text-muted">
              Mode: <strong>{previewDevice === "desktop" ? "Desktop" : "Mobile"}</strong>
            </span>
            <button
              type="button"
              className="btn btn-secondary rounded-3 px-4"
              onClick={() => setIsPreviewOpen(false)}
            >
              Close
            </button>
          </div>
        }
      >
        <div className="email-preview-wrapper">
          <div className="email-preview-toolbar">
            <span className="small text-muted fw-semibold">Device Preview:</span>
            <div className="d-flex gap-2">
              <button
                type="button"
                className={`email-preview-device-btn ${previewDevice === "desktop" ? "active" : ""}`}
                onClick={() => setPreviewDevice("desktop")}
              >
                <MdDesktopWindows size={16} />
                <span>Desktop</span>
              </button>
              <button
                type="button"
                className={`email-preview-device-btn ${previewDevice === "mobile" ? "active" : ""}`}
                onClick={() => setPreviewDevice("mobile")}
              >
                <MdPhoneIphone size={16} />
                <span>Mobile</span>
              </button>
            </div>
          </div>

          <div className={`email-preview-card ${previewDevice}`}>
            <div className="email-preview-header">
              <div className="email-preview-subject-line">
                {subjectRef.current?.trim() || "(No Subject)"}
              </div>
              <div className="email-preview-meta-row">
                <div className="email-preview-meta-item">
                  <span className="email-preview-meta-label">From:</span>
                  <span className="email-preview-meta-value">
                    Aegis Health 360 &lt;notifications@aegis360.com&gt;
                  </span>
                </div>
                <div className="email-preview-meta-item">
                  <span className="email-preview-meta-label">To:</span>
                  <span className="email-preview-meta-value">
                    recipient@example.com
                  </span>
                </div>
              </div>
            </div>

            <div className="email-preview-body">
              {hasPreviewContent ? (
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              ) : (
                <div className="email-preview-empty">
                  <div className="email-preview-empty-icon">
                    <MdDrafts size={24} />
                  </div>
                  <p className="fw-semibold mb-1 text-dark">No email content to preview</p>
                  <small>Start typing in the editor to preview your email template here.</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default TextEditor;